import { restoreDialogFocus } from './focus';
import { useEffect, useLayoutEffect, useId, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from './button';
import { Title } from './layout';
import './dialog.css';

let scrollLocks = 0;
let previousOverflow = '';
function lockPageScroll(): () => void {
  if (scrollLocks === 0) previousOverflow = document.body.style.overflow;
  scrollLocks += 1;
  document.body.style.overflow = 'hidden';
  return () => {
    scrollLocks -= 1;
    if (scrollLocks === 0) document.body.style.overflow = previousOverflow;
  };
}

/** Native focus trapping and restoration, with a bottom sheet on phones. */
export function ResponsiveDialog({
  title,
  closeLabel,
  onClose,
  children,
  footer,
}: {
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const titleId = useId();
  const [closing, setClosing] = useState(false);
  useLayoutEffect(() => {
    const element = dialog.current;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlockScroll = lockPageScroll();
    element?.showModal();
    return () => {
      element?.close();
      unlockScroll();
      restoreDialogFocus(previousFocus, element);
    };
  }, []);
  useEffect(() => {
    if (!closing) return;
    const timer = window.setTimeout(
      () => close.current(),
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180,
    );
    return () => window.clearTimeout(timer);
  }, [closing]);
  return (
    <dialog
      ref={dialog}
      className={`ui-dialog${footer ? ' ui-dialog--with-footer' : ''}${closing ? ' ui-dialog--closing' : ''}`}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setClosing(true);
      }}
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          setClosing(true);
      }}
    >
      <div className="ui-dialog__handle" aria-hidden="true" />
      <header className="ui-dialog__header">
        <Title level={2} id={titleId}>
          {title}
        </Title>
        <Button
          variant="ghost"
          size="icon"
          aria-label={closeLabel}
          onClick={() => setClosing(true)}
        >
          <X size={21} aria-hidden="true" />
        </Button>
      </header>
      <div className="ui-dialog__body">{children}</div>
      {footer && <footer className="ui-dialog__footer">{footer}</footer>}
    </dialog>
  );
}
